import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useVehicles } from '../contexts/VehicleContext'
import VehicleList from './VehicleList'
import FleetMap from './FleetMap'
import VehicleTimeline from './VehicleTimeline'
import VehicleInfo from './VehicleInfo'
import Header from './Header'
import { Vehicle } from '../types'

const FleetDashboard: React.FC = () => {
  const { t } = useTranslation()
  const { state, selectVehicle } = useVehicles()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false)
  const [timeRange, setTimeRange] = useState(168) // 7 days by default

  const handleVehicleSelect = (vehicle: Vehicle) => {
    selectVehicle(vehicle)
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <Header />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Vehicle List */}
        <div className={`bg-white border-r border-gray-200 transition-all duration-300 ${
          sidebarCollapsed ? 'w-12' : 'w-80'
        }`}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className={`font-semibold text-gray-800 ${sidebarCollapsed ? 'hidden' : 'block'}`}>
              {t('vehicle_list')}
            </h2>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1 rounded hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d={sidebarCollapsed ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
              </svg>
            </button>
          </div>
          
          {!sidebarCollapsed && (
            <VehicleList
              vehicles={state.vehicles}
              selectedVehicle={state.selectedVehicle}
              onVehicleSelect={handleVehicleSelect}
              loading={state.loading}
            />
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Map Container */}
          <div className="flex-1 relative">
            <FleetMap
              vehicles={state.vehicles}
              selectedVehicle={state.selectedVehicle}
              onVehicleSelect={handleVehicleSelect}
              timeRange={timeRange}
            />
            
            {/* Map Controls Overlay */}
            <div className="absolute top-4 left-4 z-10">
              <div className="bg-white rounded-lg shadow-lg p-2">
                <button className="p-2 hover:bg-gray-100 rounded">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Real-time Status Indicator */}
            <div className="absolute top-4 right-4 z-10">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                state.realTimeEnabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    state.realTimeEnabled ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  <span>{state.realTimeEnabled ? 'En vivo' : 'Pausado'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Container */}
          <div className="h-32 bg-white border-t border-gray-200">
            <VehicleTimeline
              vehicle={state.selectedVehicle}
              history={state.vehicleHistory}
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
            />
          </div>
        </div>

        {/* Right Sidebar - Vehicle Info */}
        <div className={`bg-white border-l border-gray-200 transition-all duration-300 ${
          rightPanelCollapsed ? 'w-12' : 'w-80'
        }`}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className={`font-semibold text-gray-800 ${rightPanelCollapsed ? 'hidden' : 'block'}`}>
              Información
            </h2>
            <button
              onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
              className="p-1 rounded hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d={rightPanelCollapsed ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
              </svg>
            </button>
          </div>
          
          {!rightPanelCollapsed && (
            <VehicleInfo vehicle={state.selectedVehicle} />
          )}
        </div>
      </div>
    </div>
  )
}

export default FleetDashboard
